import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '@shared/api';
import { clearQueryResponseCache } from '@shared/lib/query-helpers';
import { patchHelpdeskTask } from './task-edit';
import { helpdeskStatuses } from '../model/search';

const commandSchema = z.object({
  body: z.union([
    z.object({ feedbackCategoryId: z.uuid().nullable() }).strict(),
    z.object({ dueAt: z.string().nullable() }).strict(),
    z.object({ status: z.enum(helpdeskStatuses) }).strict(),
  ]),
  etag: z.string(),
  idempotencyKey: z.uuid(),
});
type FieldCommand = z.infer<typeof commandSchema>;
type EditableField = 'category' | 'due' | 'status';

function isCommand(value: unknown): value is FieldCommand {
  return commandSchema.safeParse(value).success;
}

function isUnknownOutcome(error: unknown): boolean {
  return !(isApiError(error) && error.status >= 400 && error.status < 500 && error.status !== 408);
}

function commandField(command: FieldCommand): EditableField {
  if ('feedbackCategoryId' in command.body) return 'category';
  return 'dueAt' in command.body ? 'due' : 'status';
}

/** Retain one frozen category, Due or status operation across popup and route unmounts. */
export function useHelpdeskFieldEdit(
  viewerAccessScope: string,
  taskUuid: string,
  field: EditableField
) {
  const client = useQueryClient();
  const mutationKey = ['helpdesk-task-edit', viewerAccessScope, taskUuid];
  const filters = { mutationKey, exact: true };
  const operations = useMutationState({
    filters,
    select: (mutation) => ({
      status: mutation.state.status,
      error: mutation.state.error,
      command: isCommand(mutation.state.variables) ? mutation.state.variables : undefined,
    }),
  });
  const latest = operations.at(-1);
  const ownsOperation = latest?.command && commandField(latest.command) === field;
  const pending = latest?.status === 'pending';
  const uncertain = latest?.status === 'error' && isUnknownOutcome(latest.error);
  const mutation = useMutation({
    mutationKey,
    gcTime: Infinity,
    retry: false,
    mutationFn: (command: FieldCommand) => patchHelpdeskTask(taskUuid, command),
    onSuccess: async () => {
      clearQueryResponseCache();
      await client.invalidateQueries({ queryKey: ['helpdesk', viewerAccessScope] });
    },
  });
  const submit = (command: FieldCommand, onSuccess: () => void, retry = false) => {
    // Mutation.execute marks pending synchronously, before its first await. All staff
    // editors use this same cache key, so even a second same-tick instance sees the lock.
    const previous = client.getMutationCache().findAll(filters).at(-1);
    if (previous?.state.status === 'pending') return;
    if (commandField(command) !== field) return;
    if (
      retry &&
      (!isCommand(previous?.state.variables) ||
        previous.state.variables.idempotencyKey !== command.idempotencyKey ||
        previous.state.status !== 'error' ||
        !isUnknownOutcome(previous.state.error))
    )
      return;
    if (!retry && previous?.state.status === 'error' && isUnknownOutcome(previous.state.error))
      return;
    for (const entry of client.getMutationCache().findAll(filters)) {
      if (entry.state.status !== 'pending') client.getMutationCache().remove(entry);
    }
    mutation.mutate(command, { onSuccess });
  };
  return {
    pending: Boolean(ownsOperation && pending),
    uncertain: Boolean(ownsOperation && uncertain),
    blockedByOther: !ownsOperation && (pending || uncertain),
    body: ownsOperation && (pending || uncertain) ? latest.command?.body : undefined,
    error: ownsOperation && latest.status === 'error' ? latest.error : null,
    save: (input: Omit<FieldCommand, 'idempotencyKey'>, onSuccess: () => void) => {
      submit({ ...input, idempotencyKey: crypto.randomUUID() }, onSuccess);
    },
    retry: (onSuccess: () => void) => {
      if (ownsOperation && uncertain && latest.command) submit(latest.command, onSuccess, true);
    },
    resetRejected: () => {
      for (const entry of client.getMutationCache().findAll(filters)) {
        if (
          entry.state.status === 'error' &&
          !isUnknownOutcome(entry.state.error) &&
          isCommand(entry.state.variables) &&
          commandField(entry.state.variables) === field
        )
          client.getMutationCache().remove(entry);
      }
    },
  };
}
